import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TechnicalAudit = () => {
  const auditItems = [
    {
      category: "Page Speed",
      items: [
        { name: "First Contentful Paint", value: "1.2s", status: "pass", recommendation: "Excellent performance" },
        { name: "Largest Contentful Paint", value: "2.1s", status: "pass", recommendation: "Within optimal range" },
        { name: "Time to Interactive", value: "3.8s", status: "warning", recommendation: "Consider reducing JavaScript execution time" },
        { name: "Cumulative Layout Shift", value: "0.05", status: "pass", recommendation: "Minimal layout shifts detected" }
      ]
    },
    {
      category: "Mobile Optimization",
      items: [
        { name: "Mobile-Friendly Test", value: "Passed", status: "pass", recommendation: "Page is mobile-friendly" },
        { name: "Viewport Configuration", value: "Configured", status: "pass", recommendation: "Proper viewport meta tag detected" },
        { name: "Touch Elements", value: "Optimized", status: "pass", recommendation: "Touch targets are appropriately sized" },
        { name: "Font Sizes", value: "Readable", status: "pass", recommendation: "Text is legible on mobile devices" }
      ]
    },
    {
      category: "Security & HTTPS",
      items: [
        { name: "SSL Certificate", value: "Valid", status: "pass", recommendation: "Certificate expires in 89 days" },
        { name: "HTTPS Redirect", value: "Active", status: "pass", recommendation: "All HTTP traffic redirects to HTTPS" },
        { name: "Mixed Content", value: "None", status: "pass", recommendation: "No insecure resources detected" },
        { name: "Security Headers", value: "Configured", status: "warning", recommendation: "Consider adding Content-Security-Policy header" }
      ]
    },
    {
      category: "Crawlability",
      items: [
        { name: "Robots.txt", value: "Valid", status: "pass", recommendation: "Properly configured for search engines" },
        { name: "XML Sitemap", value: "Found", status: "pass", recommendation: "Sitemap contains 47 URLs" },
        { name: "Canonical Tags", value: "Implemented", status: "pass", recommendation: "Canonical URLs properly set" },
        { name: "Broken Links", value: "3 Found", status: "fail", recommendation: "Fix broken internal links on /blog and /resources" }
      ]
    }
  ];

  const getStatusIcon = (status) => {
    if (status === 'pass') return { name: 'CheckCircle2', color: 'text-green-600' };
    if (status === 'warning') return { name: 'AlertCircle', color: 'text-amber-600' };
    return { name: 'XCircle', color: 'text-red-600' };
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 md:p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
            <Icon name="Shield" size={20} className="text-green-600" />
          </div>
          <div>
            <h2 className="text-lg md:text-xl font-semibold text-gray-900">Technical SEO Audit</h2>
            <p className="text-sm text-gray-600">Last scanned: January 14, 2026 at 9:15 AM</p>
          </div>
        </div>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left">
          Run Audit
        </Button>
      </div>
      <div className="space-y-6">
        {auditItems?.map((section, sectionIndex) => (
          <div key={sectionIndex} className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-4">{section?.category}</h3>
            <div className="space-y-3">
              {section?.items?.map((item, itemIndex) => {
                const statusInfo = getStatusIcon(item?.status);
                return (
                  <div key={itemIndex} className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                    <Icon name={statusInfo?.name} size={20} className={statusInfo?.color} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h4 className="text-sm md:text-base font-medium text-gray-900">{item?.name}</h4>
                        <span className="text-sm font-semibold text-gray-700 whitespace-nowrap ml-2">{item?.value}</span>
                      </div>
                      <p className="text-xs md:text-sm text-gray-600">{item?.recommendation}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-start gap-3">
          <Icon name="Lightbulb" size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm md:text-base font-semibold text-blue-900 mb-1">Optimization Tip</h4>
            <p className="text-xs md:text-sm text-blue-800">
              Your site scores 87/100 on technical SEO. Focus on fixing the 3 broken links and implementing Content-Security-Policy headers to reach 95+.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalAudit;